import type { AuthUser } from 'aws-amplify/auth';
import { motion } from 'framer-motion';
import { Clock, Mail, Shield, Loader2 } from 'lucide-react';

interface PendingApprovalProps {
  user: AuthUser | undefined;
}

export default function PendingApproval({ user }: PendingApprovalProps) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg mx-auto text-center"
      >
        {/* Animated Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
          className="relative w-24 h-24 mx-auto mb-8"
        >
          <div className="absolute inset-0 rounded-full bg-accent/20" />
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 rounded-full border-2 border-transparent border-t-accent/40"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Clock className="w-10 h-10 text-accent" />
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-2xl md:text-3xl font-display text-cream mb-4">
            Account Pending Approval
          </h1>

          <div className="bg-workshop-surface rounded-sm border border-white/[0.06] shadow-soft overflow-hidden mb-6">
            <div className="p-6 space-y-4">
              <p className="text-text-muted leading-relaxed">
                Thank you for registering with{' '}
                <span className="font-semibold text-cream">Top Dog Brand</span>{' '}
                Wholesale Portal.
              </p>

              {/* Steps */}
              <div className="grid gap-3 mt-6">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-start gap-3 bg-green-900/10 border border-green-800/20 rounded-sm p-3"
                >
                  <div className="w-6 h-6 rounded-full bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="w-3 h-3 text-green-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-green-300 font-medium">Account Created</p>
                    <p className="text-xs text-text-muted mt-0.5">Your registration has been received.</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-start gap-3 bg-accent/[0.08] border border-accent/20 rounded-sm p-3"
                >
                  <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Loader2 className="w-3 h-3 text-accent animate-spin" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-accent font-medium">Under Review</p>
                    <p className="text-xs text-text-muted mt-0.5">
                      We're verifying your business details. This typically takes{' '}
                      <span className="text-cream font-medium">1-2 business days</span>.
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex items-start gap-3 bg-white/[0.02] border border-white/[0.06] rounded-sm p-3 opacity-50"
                >
                  <div className="w-6 h-6 rounded-full bg-white/[0.05] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail className="w-3 h-3 text-text-muted" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-text-muted font-medium">Email Notification</p>
                    <p className="text-xs text-text-muted mt-0.5">
                      You'll receive an email at{' '}
                      <span className="text-accent">{user?.signInDetails?.loginId || 'your email'}</span>{' '}
                      once approved.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
            className="inline-flex items-center px-4 py-2 rounded-sm bg-accent/[0.08] border border-accent/30"
          >
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse mr-2" />
            <span className="text-accent text-sm uppercase tracking-wide font-medium">
              Verification in Progress
            </span>
          </motion.div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 pt-6 border-t border-white/[0.06]"
          >
            <p className="text-text-muted text-sm">
              Questions? Contact us at{' '}
              <a
                href="mailto:wholesale@topdogbrand.com"
                className="text-accent hover:text-accent-light underline underline-offset-2 transition-colors"
              >
                wholesale@topdogbrand.com
              </a>
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
